import React, { useEffect, useState } from "react";
import "./Auctions.css";

const ALL_AUCTIONS_URL =
  "https://airauctioneer.com/beth-branson-memorial-match-4-2026-auctions";

export default function Auctions() {
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadAuctions() {
      try {
        const response = await fetch("/api/auctions");

        if (!response.ok) {
          throw new Error("Could not load auctions.");
        }

        const data = await response.json();

        if (!cancelled) {
          setAuctions(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadAuctions();

    return () => {
      cancelled = true;
    };
  }, []);

  const featuredAuctions = auctions.filter(
    (auction) => auction.featured
  );

  const regularAuctions = auctions.filter(
    (auction) => !auction.featured
  );

  return (
    <section className="auctions-page">
      <div className="auctions-hero">
        <span className="auctions-kicker">
          Beth Branson Memorial Match
        </span>

        <h1>Charity Auctions</h1>

        <p>
          Explore the fantastic items and experiences donated to support
          the memorial match. Select an item to view its auction and
          place your bid through AirAuctioneer.
        </p>

        <a
          className="all-auctions-button"
          href={ALL_AUCTIONS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          View All Auctions
          <span aria-hidden="true"> ↗</span>
        </a>
      </div>

      {loading && (
        <p className="auctions-message">Loading auctions…</p>
      )}

      {error && (
        <p className="auctions-message" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && auctions.length === 0 && (
        <div className="auctions-empty">
          <h2>Auctions coming soon</h2>

          <p>
            Check back for the latest donated items and experiences.
          </p>

          <a
            className="all-auctions-button"
            href={ALL_AUCTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Visit AirAuctioneer
            <span aria-hidden="true"> ↗</span>
          </a>
        </div>
      )}

      {!loading && !error && featuredAuctions.length > 0 && (
        <section className="featured-auctions">
          <div className="auction-section-heading">
            <span className="auctions-kicker">
              Featured Auction
            </span>

            <h2>Don't miss this one</h2>
          </div>

          {featuredAuctions.map((auction) => (
            <article
              className="featured-auction-card"
              key={auction._id}
            >
              <div className="featured-auction-image">
                {auction.imageUrl ? (
                  <img
                    src={auction.imageUrl}
                    alt={auction.title}
                  />
                ) : (
                  <div className="auction-image-placeholder">
                    <span>BB3</span>
                  </div>
                )}

                <span className="auction-badge">
                  ★ Featured
                </span>

                {auction.ended && (
                  <span className="auction-badge auction-ended">
                    Auction ended
                  </span>
                )}
              </div>

              <div className="featured-auction-content">
                <span className="auction-type">
                  Featured Charity Auction
                </span>

                <h2>{auction.title}</h2>

                {auction.description && (
                  <p>{auction.description}</p>
                )}

                <a
                  className="auction-link"
                  href={auction.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {auction.ended
                    ? "View Auction"
                    : "View Auction & Bid"}

                  <span aria-hidden="true"> ↗</span>
                </a>
              </div>
            </article>
          ))}
        </section>
      )}

      {!loading && !error && regularAuctions.length > 0 && (
        <section className="all-auction-items">
          <div className="auction-section-heading">
            <span className="auctions-kicker">
              Browse
            </span>

            <h2>All Auctions</h2>
          </div>

          <div className="auctions-grid">
            {regularAuctions.map((auction) => (
              <article
                className="auction-card"
                key={auction._id}
              >
                <div className="auction-image-wrap">
                  {auction.imageUrl ? (
                    <img
                      src={auction.imageUrl}
                      alt={auction.title}
                      loading="lazy"
                    />
                  ) : (
                    <div className="auction-image-placeholder">
                      <span>BB3</span>
                    </div>
                  )}

                  {auction.ended && (
                    <span className="auction-badge auction-ended">
                      Auction ended
                    </span>
                  )}
                </div>

                <div className="auction-card-content">
                  <span className="auction-type">
                    Charity Auction
                  </span>

                  <h2>{auction.title}</h2>

                  {auction.description && (
                    <p>{auction.description}</p>
                  )}

                  <a
                    className="auction-link"
                    href={auction.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {auction.ended
                      ? "View Auction"
                      : "View Auction & Bid"}

                    <span aria-hidden="true"> ↗</span>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {!loading && !error && auctions.length > 0 && (
        <div className="auctions-footer">
          <span className="auctions-kicker">
            Thank you for your support
          </span>

          <h2>Every bid helps make a difference</h2>

          <p>
            Auctions and bidding are hosted on AirAuctioneer. Thank you
            to everyone who has donated an item or supported the Beth
            Branson Memorial Match.
          </p>

          <a
            className="all-auctions-button"
            href={ALL_AUCTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            View All Auctions on AirAuctioneer
            <span aria-hidden="true"> ↗</span>
          </a>
        </div>
      )}
    </section>
  );
}